// Progressive tabs and recommendation reads only. Existing Shopify components own cart writes.
class OdProductInformation extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.navigation = this.querySelector('[data-od-info-navigation]');
    this.tabs = [...this.querySelectorAll('[data-od-info-tab]')];
    this.panels = [...this.querySelectorAll('[data-od-info-panel]')];
    if (!this.navigation || !this.tabs.length || this.tabs.length !== this.panels.length) return;
    this.navigation.setAttribute('role', 'tablist');
    const mobile = matchMedia('(width < 750px)');
    const orient = () => this.navigation.setAttribute('aria-orientation', mobile.matches ? 'horizontal' : 'vertical');
    orient();
    mobile.addEventListener('change', orient, options);
    this.tabs.forEach(tab => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', tab.hash.slice(1));
    });
    this.panels.forEach(panel => { panel.setAttribute('role', 'tabpanel'); panel.tabIndex = 0; });
    this.setAttribute('data-enhanced', '');
    const fromHash = this.tabs.find(tab => tab.hash === location.hash);
    this.select(fromHash?.dataset.odInfoTab || this.activeTab || this.dataset.defaultTab || this.tabs[0].dataset.odInfoTab);
    this.navigation.addEventListener('click', event => {
      const tab = event.target.closest('[data-od-info-tab]');
      if (!tab || !this.navigation.contains(tab) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.select(tab.dataset.odInfoTab);
    }, options);
    this.navigation.addEventListener('keydown', event => {
      const tab = event.target.closest('[data-od-info-tab]');
      if (!tab || event.altKey || event.ctrlKey || event.metaKey) return;
      const current = this.tabs.indexOf(tab);
      let next;
      if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (current - 1 + this.tabs.length) % this.tabs.length;
      if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (current + 1) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      this.select(this.tabs[next].dataset.odInfoTab, true);
    }, options);
    window.addEventListener('hashchange', () => {
      const tab = this.tabs.find(item => item.hash === location.hash);
      if (tab) this.select(tab.dataset.odInfoTab);
    }, options);
  }

  select(name, focus = false) {
    if (!this.tabs.some(tab => tab.dataset.odInfoTab === name)) name = this.tabs[0].dataset.odInfoTab;
    this.activeTab = name;
    this.tabs.forEach(tab => {
      const selected = tab.dataset.odInfoTab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) {
        tab.focus({ preventScroll: true });
        // Keep keyboard navigation within the mobile strip without moving the document.
        const left = tab.offsetLeft - this.navigation.offsetLeft;
        if (left < this.navigation.scrollLeft) this.navigation.scrollLeft = left;
        else if (left + tab.offsetWidth > this.navigation.scrollLeft + this.navigation.clientWidth) this.navigation.scrollLeft = left + tab.offsetWidth - this.navigation.clientWidth;
      }
    });
    this.panels.forEach(panel => { panel.hidden = panel.dataset.odInfoPanel !== name; });
  }

  disconnectedCallback() { this.abort?.abort(); }
}

class OdProductRelated extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    this.generation = (this.generation || 0) + 1;
    this.abort = new AbortController();
    this.addEventListener('submit', event => {
      if (event.target.closest?.('[data-od-native-form]')) this.cartTouched = true;
    }, { signal: this.abort.signal, capture: true });
    if (this.dataset.source !== 'recommendations' || this.dataset.loaded === 'true') return;
    this.load(this.generation);
  }

  async load(generation) {
    const request = this.request = new AbortController();
    const timer = setTimeout(() => request.abort(), 12000);
    try {
      const url = new URL(this.dataset.url, location.href);
      if (url.origin !== location.origin || !/\/recommendations\/products\/?$/.test(url.pathname) || !/^\d+$/.test(this.dataset.productId)) return;
      url.searchParams.set('product_id', this.dataset.productId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('limit', String(Math.max(2, Math.min(10, Number(this.dataset.limit) || 8))));
      url.searchParams.set('intent', 'related');
      if (this.dataset.productView === 'od-product') url.searchParams.set('view', 'od-product');
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { credentials: 'same-origin', signal: request.signal });
      if (!response.ok) throw new Error(`Recommendations response ${response.status}`);
      const html = await response.text();
      if (!this.isConnected || request.signal.aborted || this.request !== request || this.generation !== generation) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remote = [...doc.querySelectorAll('od-product-related')].find(element => element.dataset.sectionId === this.dataset.sectionId && element.dataset.productId === this.dataset.productId && element.dataset.loaded === 'true');
      const incoming = remote?.querySelector('[data-od-related-content]');
      const current = this.querySelector('[data-od-related-content]');
      if (!incoming || !current) throw new Error('Missing recommendation section');
      // Preserve a manual fallback once the customer has interacted with its cart forms.
      // No in-flight native form, error notice or focused control is detached by this read.
      const carousel = current.querySelector('od-carousel');
      if (this.cartTouched || carousel?.pending?.size || current.querySelector('[data-od-pending],form[aria-busy="true"]') || current.contains(document.activeElement)) {
        this.dataset.recommendationsDeferred = 'true';
        return;
      }
      this.retainProductView(incoming);
      current.replaceChildren(...[...incoming.childNodes].map(node => document.importNode(node, true)));
      this.dataset.loaded = 'true';
      this.hidden = remote.hidden;
      this.removeAttribute('data-load-error');
    } catch (error) {
      if (!this.isConnected || this.request !== request || this.generation !== generation) return;
      // Keep real manual fallback cards; an optional failed read never affects product forms.
      this.setAttribute('data-load-error', '');
    } finally {
      clearTimeout(timer);
    }
  }

  retainProductView(scope) {
    // Shopify recommendations SRA can render outside the product template suffix context.
    if (this.dataset.productView !== 'od-product') return;
    scope.querySelectorAll('a[href]').forEach(link => {
      try {
        const url = new URL(link.getAttribute('href'), location.href);
        if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return;
        url.searchParams.set('view', 'od-product');
        link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      } catch (_) { /* Leave unsupported links untouched. */ }
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    this.generation = (this.generation || 0) + 1;
  }
}

if (!customElements.get('od-product-information')) customElements.define('od-product-information', OdProductInformation);
if (!customElements.get('od-product-related')) customElements.define('od-product-related', OdProductRelated);
